
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { ContactList } from "@/components/contacts/ContactList";
import { ContactForm } from "@/components/contacts/ContactForm";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Header } from "@/components/Header";
import { useAuth } from "@/contexts/AuthContext";
import { useOrganization } from "@/contexts/OrganizationContext";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, Plus } from "lucide-react";

const Contacts = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const { session, isLoading: authLoading } = useAuth();
  const { currentOrganization, isLoading: orgLoading, organizations } = useOrganization();

  useEffect(() => {
    if (!authLoading && !session) {
      navigate("/auth");
    }
  }, [session, authLoading, navigate]);

  // Debug logging for organization state
  useEffect(() => {
    console.log('📊 Contacts page: Organization state', {
      currentOrganization,
      orgLoading,
      organizationsCount: organizations.length,
      hasOrganizations: organizations.length > 0
    });
  }, [currentOrganization, orgLoading, organizations]);

  const isLoading = authLoading || orgLoading;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!session) {
    return null; // Will redirect in useEffect
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="container mx-auto py-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold">Contacts</h1>
            {currentOrganization && (
              <p className="text-muted-foreground mt-1">
                {currentOrganization.name} (ID: {currentOrganization.id})
              </p>
            )}
          </div>
          
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button variant="outline">
                <Plus className="h-4 w-4 mr-2" />
                Add Contact
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[600px]">
              <DialogHeader>
                <DialogTitle>Add New Contact</DialogTitle>
                <DialogDescription>
                  Add a new contact to your organization. Fill in the details below.
                </DialogDescription>
              </DialogHeader>
              <ContactForm onSuccess={() => {
                setOpen(false);
                toast({
                  title: "Contact added successfully",
                  description: "The contact has been added to your list."
                });
              }} />
            </DialogContent>
          </Dialog>
        </div>

        {!currentOrganization ? (
          <Alert className="my-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Please select an organization to view and manage contacts.
            </AlertDescription>
          </Alert>
        ) : (
          <ContactList />
        )}
      </div>
    </div>
  );
};

export default Contacts;
